import { useEffect, useState } from "react"
import { View, Pressable, SafeAreaView } from "react-native"
import CompsTabContent from "@/components/analysis/CompsTabContent"
import CalculateTabContent from "@/components/analysis/calculate/CalculateTabContent"
import { type AnalysisType } from "@/generated/graphql"
import { useViewedCallback } from "@/analytics/react"
import { useStore } from "@nanostores/react"
import { currentStrategy } from "@/stores/strategyStore"

import * as AnalyzerEvents from "@/analytics/events/analyzer"
import BFText from "@/components/standard/BFText"
import { cn } from "@/components/util/cn"
import CompSelectionIntroOverlay from "../compselection/CompSelectionIntroOverlay"
import { useAnalyzeContext } from "@/components/analysis/AnalyzeContext"

interface CompsAndCalculateTabsProps {
  analysis: AnalysisType | null
}

type Tabs = "Comps" | "Calculate"

const CompsAndCalculateTabs: React.FC<CompsAndCalculateTabsProps> = ({
  analysis,
}: CompsAndCalculateTabsProps) => {
  const [activeTab, setActiveTab] = useState<Tabs>("Comps")
  const currentCalculatorStrategy = useStore(currentStrategy)

  useEffect(() => {
    if (!analysis) {
      setActiveTab("Comps")
    }
  }, [analysis])

  useViewedCallback(() => {
    if (!analysis?.id) {
      return
    }

    AnalyzerEvents.subjectPropertyViewed({
      analysis,
      calculatorStrategy: currentCalculatorStrategy,
      activeTab,
    })
  }, [analysis?.id, analysis?.distance, activeTab, currentCalculatorStrategy])

  const { setIsSelectForMeChosen } = useAnalyzeContext()

  return (
    <SafeAreaView>
      <CompSelectionIntroOverlay
        onSelectForMe={() => {
          AnalyzerEvents.compSelectionAutoSelectClicked(analysis)
          setIsSelectForMeChosen(true)
          return undefined
        }}
      />

      <View className="w-full mt-4 bg-off-white">
        {/* Tab buttons */}
        <View className="flex-row justify-center mb-[14px]">
          <TabSelector
            setActiveTab={setActiveTab}
            label={"Comps"}
            isActive={activeTab === "Comps"}
          />

          <TabSelector
            setActiveTab={setActiveTab}
            label={"Calculate"}
            isActive={activeTab === "Calculate"}
          />
        </View>

        <View className="absolute top-[24px] w-full h-px bg-gray-300 opacity-40" />

        {analysis && (
          <View>
            {activeTab === "Comps" ? (
              <CompsTabContent
                analysis={analysis}
                onEnterArv={() => {
                  setActiveTab("Calculate")
                }}
              />
            ) : (
              <CalculateTabContent
                analysisId={analysis.id}
                onEnterArv={() => {
                  setActiveTab("Calculate")
                }}
              />
            )}
          </View>
        )}
      </View>
    </SafeAreaView>
  )
}

interface TabSelectorProps {
  label: Tabs
  isActive: boolean
  setActiveTab: (activeTab: Tabs) => void
}

const TabSelector: React.FC<TabSelectorProps> = ({
  label,
  isActive,
  setActiveTab,
}: TabSelectorProps) => {
  return (
    <Pressable
      testID={`tab-${label}`}
      onPress={() => {
        setActiveTab(label)
      }}
      className={cn(["flex-1 px-8"])}
    >
      <View
        className={`justify-stretch mx-1 border-b-[3px] ${isActive ? "border-b-calm-blue" : "border-b-off-white"}`}
      >
        <BFText
          className={`${isActive ? "text-calm-blue" : "text-midnight-blue"} font-figtree-extrabold text-center text-[19px] mb-px leading-tight`}
        >
          {label}
        </BFText>
      </View>
    </Pressable>
  )
}

export default CompsAndCalculateTabs
